// One game's facts, from the reader's side: what every count on the
// dashboard is made of. The store keeps one per game it has read. Every field
// is declared here, so any plugin reading a fact sees its type; the engine's
// facts are written by the fact extractors (`@chess/api-insights`,
// `facts/`), one file each, named beside the fields they write.
import type { Color, Fen, San, Uci } from './chess.ts';
import type { Conversion, EndingResult, EndingStart, EndingType, GameEnding, GameOutcome, GroundCause, LostPiece, MiddlegamePosition, MiddlegameResult, RatingBand, StageStanding, TimeClass } from './classification.ts';
import type { MoveGrade, MoveKind, Phase } from './scan.ts';

/** How a game ended: the filter's endings, and `other` when the Termination header names none of them. */
export type Ending = GameEnding | 'other';
export const ENDINGS = ['checkmate', 'resignation', 'time', 'abandoned', 'draw', 'other'] as const satisfies readonly Ending[];

/**
 * How the reader's ending went, the first case that matches: `won` the
 * reader won; `time` lost by timeout, whatever the position; `slipped` not
 * won, and the reader stood winning (the rules' `winningPawns`) before one
 * of their ending moves; `missed` not won, never winning there, but one of
 * their ending moves passed over a winning line; `lost` lost with no winning
 * chance in the ending; `drawn` drawn with none.
 */
export type EndingCase = 'won' | 'time' | 'slipped' | 'missed' | 'lost' | 'drawn';
export const ENDING_CASES = ['won', 'time', 'slipped', 'missed', 'lost', 'drawn'] as const satisfies readonly EndingCase[];

export interface GameFacts {
  id: string;
  /** Epoch milliseconds. */
  playedAt: number;
  colour: Color;
  outcome: GameOutcome;
  timeClass?: TimeClass;
  readerRating?: number;
  opponentRating?: number;
  ending: Ending;
  /** The opponent against the reader by rating; undefined without both ratings. */
  ratingBand?: RatingBand;
  /** The opening's family, e.g. "Sicilian Defense": a prefix of the game's opening name, so the filter's opening match finds it. */
  opening?: string;
  /** The opening's variation as the book writes it, e.g. "Sicilian Defense: Alapin"; the family alone for its main line. */
  variation?: string;
  /** Whether the game's scan is complete; the engine's facts are set only then. */
  scanned: boolean;
  /** The opponent's name. */
  opponent?: string;
  /** The game's review address, when the record names its source. */
  reviewPath?: string;

  // The engine's facts, scanned games only, each written by one fact
  // extractor (`@chess/api-insights`, `facts/<file>`).
  // ending-blunders.ts
  /** The reader's moves in the ending that lost a winning position, in games the reader lost; scanned games only. */
  endingBlunders?: EndingBlunder[];
  /** The reader stood winning before one of their moves in the ending; scanned games only. */
  winningEnding?: boolean;

  // ending-case.ts
  /** How the reader's ending went (`EndingCase`); scanned games that reached an ending only. */
  endingCase?: EndingCase;
  /** The kind of ending when it started (`EndingType`); scanned games that reached an ending only. */
  endingType?: EndingType;
  /** How the ending's first position stood for the reader (`EndingStart`, from the Winning setting); scanned games that reached an ending with a score there only. */
  endingStart?: EndingStart;
  /** The ending result (`EndingResult`), from `endingStart` and the game's outcome; set with `endingStart`. */
  endingResult?: EndingResult;
  /** How the middlegame's first position stood for the reader (`StageStanding`, from the Winning setting); scanned games that reached a middlegame with a score there only. */
  middlegameStart?: StageStanding;
  /** How the middlegame ended for the reader: the standing of its last position when the game went on to an ending, else the game's outcome (won → winning, drawn → level, lost → lost); set with `middlegameStart`. */
  middlegameEnd?: StageStanding;
  /** The middlegame result (`MiddlegameResult`), from `middlegameStart` and `middlegameEnd`; set with them. */
  middlegameResult?: MiddlegameResult;

  // ground-cause.ts
  /** Why the middlegame went wrong (`GroundCause`), when its result is Blown advantage or Behind and it holds a counted mistake. */
  groundCause?: GroundCause;
  /** The move to practise for it: the costliest of the reader's middlegame mistakes of that cause; set with `groundCause`. */
  groundPractice?: { ply: number; san: string };

  // middlegame-positions.ts
  /**
   * The reader's middlegame by kind of position (`MiddlegamePosition`): for
   * each kind the reader made at least `MIDDLEGAME_POSITION_MIN_MOVES` moves
   * in, those moves and the score change they carry. Each of the reader's
   * middlegame moves carries the change since their previous move (the
   * opponent's move and their own), from the reader's side, capped, so the
   * changes of all their moves add up to the middlegame's swing; it goes to
   * every kind of the position the move was played in. Scanned games that
   * reached a middlegame only.
   */
  middlegamePositions?: Partial<Record<MiddlegamePosition, PositionShare>>;
  // conversion.ts
  /** The reader stood winning (the rules' `winningPawns`) before one of their middlegame moves: from which move, and, when the game was not won, where the lead went (`GroundCause`, the same rule as `groundCause` over the reader's counted mistakes from that move to the end, `time` for a loss on time; absent when none explains it). Scanned games only. */
  conversion?: MiddlegameConversion;

  // judgements.ts
  /** The reader's moves judged a blunder, the whole game. */
  blunders?: number;
  /** The reader's moves judged a miss, the whole game. */
  misses?: number;

  // stage-errors.ts
  /** The reader's errors in the opening (named so as not to clash with `opening`, the family); scanned games only. */
  openingErrors?: StageErrors;
  /** The reader's errors in the middlegame; scanned games that reached one only. */
  middlegame?: StageErrors;
  /** The same for the ending (named so as not to clash with `ending`, how the game ended); scanned games that reached one only. */
  endgame?: StageErrors;

  // mistakes.ts
  /** Each of the reader's moves the review lists (a blunder or a miss), in move order, with what the classifier needs; scanned games only. */
  mistakes?: MistakeFact[];

  // opening-moves.ts
  /** The reader's moves in the opening with the app's grade, in move order; scanned games only. */
  openingMoves?: GradedMove[];
  /** Where the game left the opening book: the move out of it (1-based ply) and who played it; absent when no book read the game or it never left the book. */
  bookExit?: { ply: number; by: Color };

  // opening-slips.ts
  /** The reader's moves in the opening that gave up at least `OPENING_SLIP_PAWNS` against the engine's move, in move order; scanned games only. */
  openingSlips?: OpeningSlip[];

  // stage-scores.ts
  /** Pawns, the reader's score at their first move out of the opening. */
  openingScore?: number;
  /** Pawns, the score change across each stage the game reached. */
  stageSwings?: Partial<Record<Phase, number>>;

  // accuracy.ts
  /** The reader's accuracy over the game, 0–100, after Lichess's formula; scanned games only. */
  accuracy?: number;
  /** The reader's accuracy over each stage's moves, 0–100, by the same formula as `accuracy`; a stage with fewer than `STAGE_ACCURACY_MIN_MOVES` reader moves is absent; scanned games only. */
  stageAccuracy?: Partial<Record<Phase, number>>;

  // time-slip.ts
  /** The reader's first move that let a winning position slip (the rules' `winningPawns` to under `heldPawns`) with under `TIME_SLIP_SECONDS` left on their clock, whatever the result; scanned games with clock times only. */
  timeSlip?: TimeSlip;

  // stage-curves.ts
  /** Each stage the game reached: the reader's score ply by ply and where the reader blundered or missed; scanned games only. */
  stageCurves?: Partial<Record<Phase, StageCurve>>;
}

/**
 * One stage of one game, from the reader's side: the score after each ply
 * from the stage's start to its end, and the reader's moves in it the review
 * lists. What a "last game" chart draws, and where its link opens.
 */
export interface StageCurve {
  /** The stage covers the moves after `fromPly` up to `toPly` (1-based plies). */
  fromPly: number;
  toPly: number;
  /** Pawns, capped, after each ply from `fromPly` to `toPly`; null where the scan has no score. */
  scores: Array<number | null>;
  /** The reader's moves in the stage judged a blunder or a miss. */
  marks: Array<{ ply: number; san: string; kind: MoveKind }>;
  /**
   * The reader's seconds left, aligned with `scores` (index i is ply
   * `fromPly + i`): after the reader's own move its clock, at the opponent's
   * plies the reader's last known value, null before any is known. Absent
   * when the PGN has no clocks (2026-10-10).
   */
  clock?: Array<number | null>;
}

/** One stage's errors in one game, the reader's moves only, by the review's own judgements; every stage's are the same shape. */
export interface StageErrors {
  /** Moves judged a blunder. */
  blunders: number;
  /** Moves judged a miss: a chance not taken. */
  misses: number;
  /** The piece each blunder gave away, read from the engine's reply; blunders that cost no material are left out. */
  lost: LostPiece[];
  /** Blunders or misses that took a winning position (`WINNING_PAWNS` up) to one under `HELD_PAWNS`, whatever the result. */
  thrown: number;
  /** The reader stood winning before one of their moves in the stage. */
  winning: boolean;
  /** The reader's first move in the stage judged a blunder (1-based ply), the one a practice link opens. */
  firstBlunder?: { ply: number; san: string };
}

/**
 * A move of the reader's in the ending that took a winning position, at
 * least `WINNING_PAWNS` up, to one under `HELD_PAWNS`. Only moves the review
 * lists (a blunder or a miss) count, so each one is in the stage's moments
 * table and has a puzzle.
 */
export interface EndingBlunder {
  /** The move's 1-based ply. */
  ply: number;
  san: string;
  /** Pawns from the reader's side, capped: before the move and after it. */
  before: number;
  after: number;
}

/**
 * One of the reader's moves the review lists, a blunder or a miss, as the
 * scan read it: enough to classify it (plugin `shared-mistakes`) without
 * the scan. Scores are pawns from the reader's side, capped.
 */
export interface MistakeFact {
  /** The move's 1-based ply. */
  ply: number;
  san: San;
  uci: Uci;
  kind: MoveKind;
  stage: Phase;
  /** The position before the move, the reader to move. */
  fenBefore: Fen;
  /** The position after it, the opponent to move. */
  fenAfter: Fen;
  /** The reader's score before the move (the best line's) and after it. */
  before: number;
  after: number;
  /** The engine's best line from the position before: the move the reader should have played first. */
  best?: { pv: Uci[]; mate?: number };
  /** The engine's best reply after the move: how the opponent punishes it. */
  reply?: { pv: Uci[]; mate?: number };
  /** A blunder's material given away, net, from the reply; `mate` for a forced mate against the reader. */
  lost?: LostPiece;
  /** A miss's material the best line would have won, net; `mate` for a forced mate the reader had. */
  missed?: LostPiece;
  /** How the reader stood before the move (`before`), by the Winning setting (`MistakeStanding`); absent in facts read before 2026-10-10. */
  standing?: MistakeStanding;
  /** Seconds on the reader's clock before the move: what it showed after their previous move; absent when the PGN has no clocks or before their first clocked move. */
  clock?: number;
}

/**
 * How the reader stood before a blunder or a miss, from their score and the
 * Winning setting (`rules.winningPawns`): `winning` at it or more, `losing`
 * at minus it or less, `level` between. The same lines as `StageStanding`,
 * named for a moment in the game rather than a stage's end.
 */
export type MistakeStanding = 'winning' | 'level' | 'losing';
export const MISTAKE_STANDINGS = ['winning', 'level', 'losing'] as const satisfies readonly MistakeStanding[];

/** One of the reader's moves with the app's grade of it (`MoveGrade`). */
export interface GradedMove {
  /** The move's 1-based ply. */
  ply: number;
  san: San;
  grade: MoveGrade;
  /** The position after the move, as the scan has it; absent where the scan lacks it (2026-10-10). */
  fen?: Fen;
}

/**
 * A move of the reader's in the opening that was not the engine's and gave
 * up at least `OPENING_SLIP_PAWNS`. The same position and move across games
 * is a habit: the Openings screen's "Positions you keep getting wrong".
 */
export interface OpeningSlip {
  /** The move's 1-based ply. */
  ply: number;
  san: San;
  uci: Uci;
  /** The position before the move, the reader to move. */
  fen: Fen;
  /** The game's moves up to that position, from the start. */
  line: San[];
  /** The engine's move from that position. */
  best: Uci;
  /** Pawns from the reader's side, capped: before the move (the best line's) and after it. */
  before: number;
  after: number;
}

/** The reader's moves in one kind of middlegame position, in one game (`GameFacts.middlegamePositions`). */
export interface PositionShare {
  /** The reader's moves played in a position of this kind. */
  moves: number;
  /** Pawns, the score change those moves carry, from the reader's side. */
  change: number;
}

/** A game counts in a kind of middlegame position when the reader made at least this many moves in it, so a passing moment (a trade half done) does not. */
export const MIDDLEGAME_POSITION_MIN_MOVES = 3;

/** A move in the opening that gives up this many pawns or more against the engine's is a slip. */
export const OPENING_SLIP_PAWNS = 0.3;

/** A stage's accuracy (`GameFacts.stageAccuracy`) needs at least this many of the reader's moves in it, scored. */
export const STAGE_ACCURACY_MIN_MOVES = 3;

/** A winning position let slip with the clock nearly out (`GameFacts.timeSlip`). */
export interface TimeSlip {
  /** The move's 1-based ply. */
  ply: number;
  san: string;
  /** Seconds left on the reader's clock after the move. */
  seconds: number;
}

/** Under this many seconds left after the move, a slipped win counts as a time slip. */
export const TIME_SLIP_SECONDS = 60;

/** A middlegame advantage and what became of it (`GameFacts.conversion`). */
export interface MiddlegameConversion {
  /** The reader's first middlegame move (1-based ply) made from a winning position. */
  from: number;
  /** Not won: the cause that cost the most win chance from `from` on, or `time` for a loss on time; absent when the game was won or no counted mistake explains it. */
  cause?: GroundCause;
  /** The move to practise for it: the costliest of the reader's mistakes of that cause; set with `cause` unless the cause is a flag fall with no such move. */
  practice?: { ply: number; san: string };
}

/** The library's `conversion` sets a game belongs to (`Conversion`): none unless the reader stood winning in the middlegame; `ahead`, and when not won its cause or `none`. */
export function conversionsOf(game: Pick<GameFacts, 'conversion' | 'outcome' | 'scanned'>): Conversion[] {
  if (!game.scanned || !game.conversion) return [];
  if (game.outcome === 'win') return ['ahead'];
  return ['ahead', game.conversion.cause ?? 'none'];
}
